import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('tracking_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('tracking_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Track page visit
const trackPageVisit = async (path: string, title: string, language: string) => {
  try {
    const sessionId = getSessionId();
    
    await supabase.from('page_visits').insert({
      session_id: sessionId,
      page_path: path,
      page_title: title,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      language: language,
      device_type: getDeviceType(),
    });
  } catch (error) {
    // Silently fail - don't break user experience
    console.debug('Tracking error:', error);
  }
};

// Track custom event
export const trackEvent = async (
  eventName: string, 
  eventData?: Record<string, any>,
  path?: string
) => {
  try {
    const sessionId = getSessionId();
    
    await supabase.from('custom_events').insert({
      session_id: sessionId,
      event_name: eventName,
      event_data: eventData || null,
      page_path: path || window.location.pathname,
    });

    // Also send to Plausible if available
    if (window.plausible) {
      window.plausible(eventName, { props: eventData });
    }
  } catch (error) {
    console.debug('Event tracking error:', error);
  }
};

// Main hook for automatic page tracking
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Get current language from path
    const language = location.pathname.startsWith('/dk') ? 'dk' : 'en';
    
    // Track page visit
    trackPageVisit(
      location.pathname,
      document.title,
      language
    );

    // Also track in Plausible
    if (window.plausible) {
      window.plausible('pageview');
    }
  }, [location]);
};

// TypeScript declaration for window.plausible
declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: Record<string, any> }) => void;
  }
}
